'use client';

import { WorkoutState, useWorkoutStore } from '@/app/stores/workoutStore';

import { FiWatch } from 'react-icons/fi';
import Loading from '@/app/components/Loading';
import SportIcon from '@/app/activities/SportIcon';
import { WorkoutChart } from '@/app/workouts/WorkoutChart';
import { WorkoutStatus } from '@/utils/prismaEnums';
import type {
  ActivityWithRelations,
  WorkoutWithRelations,
} from '@/utils/types';
import { cn } from '@/utils/uiUtils';
import { formatDistance } from '@trainme/api/client';
import { secondsToHm } from '@/utils/timeUtils';
import { useUnitSystem } from '@/app/hooks/useUnitSystem';
import { useCalendarState } from '@/app/calendar/utils/useCalendarState';
import { useGarminSyncButton } from '@/app/workouts/hooks/useGarminSyncButton';
import { addWeeks, startOfWeek } from 'date-fns';

type Props = {
  date: Date;
  activities: ActivityWithRelations[];
  workouts: WorkoutWithRelations[];
  active: boolean;
  openEditModal?: () => void;
  isLoading?: boolean;
  reschedulingWorkoutId?: string | null;
};

export function CalendarDayWorkouts({
  date,
  activities,
  workouts,
  active = false,
  openEditModal,
  isLoading = false,
  reschedulingWorkoutId = null,
}: Props) {
  const { setWorkout: setEditorWorkout, setActivities: setEditorActivities } =
    useCalendarState();

  const editWorkout = (workout: WorkoutWithRelations) => {
    setEditorActivities(activities);
    setEditorWorkout(workout);
    openEditModal?.();
  };

  const hasWorkouts = workouts && workouts.length > 0;

  // Only show loading if we're loading AND there's no data yet
  // This prevents showing loading spinner when scrolling through already-loaded data
  return (
    <>
      {!hasWorkouts &&
        (isLoading ? (
          <div className='border border-amber-400/30 rounded-2xl p-6 min-h-[120px] flex items-center justify-center'>
            <Loading size={16} className='text-amber-400 dark:text-amber-800' />
          </div>
        ) : (
          <div className='min-h-14 opacity-0 pointer-events-none' />
        ))}
      {hasWorkouts && (
        <ul>
          {workouts.map((workout) => (
            <li
              key={workout.id}
              className={cn(
                'cursor-pointer border-l-4 rounded-lg mt-1 overflow-hidden relative',
                workout.status === WorkoutStatus.COMPLETED
                  ? 'border-amber-400'
                  : workout.status === WorkoutStatus.SKIPPED
                    ? 'border-gray-400'
                    : 'border-amber-400',
                reschedulingWorkoutId === workout.id &&
                  'opacity-50 pointer-events-none'
              )}
              draggable={reschedulingWorkoutId !== workout.id}
              onDragStart={(event) =>
                event.dataTransfer.setData('workoutId', workout.id)
              }
              onClick={() => editWorkout(workout)}
            >
              {reschedulingWorkoutId === workout.id && (
                <div className='absolute inset-0 flex items-center justify-center gap-2 bg-gray-200 bg-opacity-50 dark:bg-gray-700 dark:bg-opacity-50 rounded-lg z-10'>
                  <Loading
                    size={24}
                    className='text-gray-600 dark:text-gray-400'
                  />
                  <span className='text-gray-700 dark:text-gray-300 font-medium'>
                    rescheduling...
                  </span>
                </div>
              )}
              <CalendarDayWorkout
                date={date}
                workout={workout}
                active={active}
              />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

const isDateInCurrentOrNextWeek = (date: Date) => {
  const thisWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
  const nextWeekStart = addWeeks(thisWeekStart, 1);
  const targetWeekStart = startOfWeek(date, { weekStartsOn: 1 });
  return (
    targetWeekStart.getTime() === thisWeekStart.getTime() ||
    targetWeekStart.getTime() === nextWeekStart.getTime()
  );
};

// New component for rendering the workout details
type CalendarDayWorkoutProps = {
  date: Date;
  workout: WorkoutWithRelations;
  active: boolean;
};

export function CalendarDayWorkout({ date, workout }: CalendarDayWorkoutProps) {
  const { setWorkout: setEditorWorkout } = useCalendarState();
  const unitSystem = useUnitSystem();
  const mergeWorkouts = useWorkoutStore(
    (state: WorkoutState) => state.mergeWorkouts
  );
  // Removed: const utils = trpc.useUtils(); // Now handled within useGarminSyncButton

  const {
    isLoading: isGarminActionLoading,
    handleGarminAction,
    buttonTitle,
    colorClassName,
  } = useGarminSyncButton({
    workout,
    onWorkoutUpdate: (updatedWorkout) => {
      mergeWorkouts([updatedWorkout]);
      setEditorWorkout(updatedWorkout);
    },
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case WorkoutStatus.COMPLETED:
        return 'bg-yellow-200 bg-opacity-20';
      case WorkoutStatus.SKIPPED:
        return 'bg-gray-400 bg-opacity-20';
      case WorkoutStatus.PENDING:
      default:
        return 'bg-yellow-200 bg-opacity-20';
    }
  };

  const shouldShowSyncButton = isDateInCurrentOrNextWeek(date);

  // New state for managing the confirmation modal
  return (
    <div
      className={cn(
        'card flex h-full w-full flex-col rounded-lg',
        getStatusColor(workout.status)
      )}
      onClick={() => setEditorWorkout(workout)}
    >
      <div className='card-header p-2'>
        <SportIcon type={workout?.sportType || ''} withColor={false} />
        <div
          className={cn(
            'flex-1 text-nowrap overflow-hidden text-ellipsis',
            workout.status === WorkoutStatus.SKIPPED && 'line-through'
          )}
          title={workout.name || ''}
        >
          {workout.name}
        </div>
      </div>
      <div className='card-body flex-1 p-2 pb-4'>
        {workout && (
          <div className='w-full h-16'>
            <WorkoutChart
              createKey={`calendar-${date.getTime()}`}
              workout={workout}
              bottomMarginOffset={3}
              compact
            />
          </div>
        )}
      </div>
      <div className='card-footer flex items-center justify-between gap-4 p-2'>
        <div className='flex gap-3 items-center'>
          <div>{secondsToHm((workout.duration || 0) * 60)}</div>
          <div>
            {workout.distance &&
              formatDistance((workout.distance || 0) * 1000, unitSystem)}
          </div>
        </div>
        {shouldShowSyncButton && (
          <button
            type='button'
            className={cn(
              'inline-flex items-center justify-center rounded-full p-1 transition-opacity',
              (!workout.id || isGarminActionLoading) &&
                'cursor-not-allowed opacity-50',
              isGarminActionLoading && 'cursor-wait opacity-80'
            )}
            disabled={!workout.id || isGarminActionLoading}
            onClick={(e) => {
              e.stopPropagation();
              handleGarminAction();
            }}
            title={buttonTitle}
          >
            <FiWatch
              className={cn(
                isGarminActionLoading && 'animate-spin',
                colorClassName
              )}
            />
            <span className='sr-only'>{buttonTitle}</span>
          </button>
        )}
      </div>
    </div>
  );
}
